import { useEffect, useMemo, useState } from 'react';
import { type ShouldRevalidateFunction } from 'react-router';
import type { CapiPlp, GridSize } from 'types/capi/plp';
import * as uuid from 'uuid';

import Content from '~/components/Content';
import Paragraphs from '~/components/Content/Paragraphs';
import DownloadAppBlock from '~/components/DownloadAppBlock';
import PageHeader from '~/components/PageHeader';
import PopularReleasesCarousel from '~/components/PopularReleasesCarousel';
import RecommendedArticles from '~/components/RecommendedArticles';
import ScrollFade from '~/components/ScrollFade';
import PlpViewEvent from '~/events/PlpViewEvent';
import SearchViewEvent from '~/events/SearchViewEvent';
import usePageData from '~/hooks/usePageData';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';
import useViewEvent from '~/hooks/useViewEvent';

import Advert from './components/Advert';
import FilterOptions from './components/FilterOptions';
import Menus from './components/Menus/Menus';
import { TopModuleSection } from './components/ModuleSection';
import NotificationTooltip from './components/NotificationTooltip';
import Products from './components/Products';
import SubMenu from './components/SubMenu';
import { getPlpViewProperties } from './plpViewProperties';

const POPULAR_CAROUSEL_MODULE_TYPE = 'plp-popular-carousel';
const POPULAR_CAROUSEL_MODULE_POSITION = 1;

function FilterBar({
  filterOptions,
  menus
}: {
  filterOptions: CapiPlp['filter_options'];
  menus: CapiPlp['menus'];
}) {
  const hasMenus = menus.length > 0;
  const hasFilterOptions = filterOptions.length > 1;

  if (!hasMenus && !hasFilterOptions) return null;

  return (
    <ScrollFade
      className="flex h-full items-center gap-2 px-4 md:px-8 desktop:px-0"
      fadeClassName="from-grey-tile desktop:hidden"
      wrapperClassName="-mx-4 mt-auto h-full md:-mx-8 desktop:mx-0"
    >
      {hasFilterOptions && <FilterOptions options={filterOptions} />}
      {hasMenus && hasFilterOptions && (
        <span className="shrink-0 px-1 text-grey-hide">•</span>
      )}
      {hasMenus && <Menus menus={menus} />}
    </ScrollFade>
  );
}

export default function PlpView({
  allowNotifications = true,
  showAppBanner = true
}: {
  allowNotifications?: boolean;
  showAppBanner?: boolean;
  showPromotion?: boolean;
}) {
  const plp = usePageData<CapiPlp>();

  const isUpcoming = plp.filter_options.some(
    (o) => o.text === 'Upcoming' && o.active
  );

  const user = useUser();
  const { clientIp, isBot, session } = useRootData();
  const [gridSize, setGridSize] = useState<GridSize>('small');

  const [introContent] = plp.content ?? [];
  const introParagraphs =
    introContent?.type === 'paragraphs' ? introContent : undefined;
  const intro =
    plp.sub_title || introParagraphs ? (
      <>
        {plp.sub_title && <p className="flex text-sm">{plp.sub_title}</p>}
        {introParagraphs && (
          <div className="hidden text-grey-read md:block lg:w-3/4">
            <Paragraphs collapsible paragraphs={introParagraphs} size="small" />
          </div>
        )}
      </>
    ) : undefined;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const listId = useMemo(() => uuid.v4(), [plp]);

  useStructuredData(plp._schemas);

  useViewEvent({
    viewProperties: useMemo(() => getPlpViewProperties(plp), [plp]),
    viewType: 'plp'
  });

  useEffect(() => {
    const isSearch = new URLSearchParams(window.location.search).has('q');

    const eventData = {
      clientIp: clientIp ?? undefined,
      isBot,
      listId,
      plp,
      user
    };

    const viewEvent = isSearch
      ? new SearchViewEvent(eventData)
      : new PlpViewEvent(eventData);

    viewEvent.send();
  }, [clientIp, plp, session, user, isBot, listId]);

  return (
    <>
      {plp.advert && <Advert advert={plp.advert} />}
      <TopModuleSection />
      <div className="flex w-full flex-col overflow-x-clip bg-grey-tile">
        <PageHeader
          actions={
            <FilterBar filterOptions={plp.filter_options} menus={plp.menus} />
          }
          breadcrumbs={plp.breadcrumbs}
          hasFilters
          stackActions
          title={plp.title}
        >
          {intro}
        </PageHeader>
        <div className="content-width w-full">
          <div
            className={`flex flex-col gap-4 pt-3 ${gridSize === 'large' ? 'md:flex-row md:items-center' : 'xs:flex-row xs:items-center'}`}
          >
            {isUpcoming && allowNotifications && (
              <NotificationTooltip gridSize={gridSize} />
            )}
            <SubMenu setGridSize={setGridSize} gridSize={gridSize} />
          </div>
          <Products
            allowNotifications={allowNotifications}
            feature={
              plp.popular_products && (
                <PopularReleasesCarousel
                  products={plp.popular_products}
                  title="Most popular releases"
                  description="The pairs with most notifications set in the last 7 days"
                  modulePosition={POPULAR_CAROUSEL_MODULE_POSITION}
                  moduleType={POPULAR_CAROUSEL_MODULE_TYPE}
                  pagePosition="PLP Popular Carousel"
                  pageType="plp"
                  allowNotifications
                  showBackground
                />
              )
            }
            gridSize={gridSize}
            listId={listId}
            modulePosition={
              plp.popular_products
                ? POPULAR_CAROUSEL_MODULE_POSITION + 1
                : POPULAR_CAROUSEL_MODULE_POSITION
            }
          />
        </div>
      </div>
      <div className="bg-concrete">
        {plp.recent_articles && (
          <RecommendedArticles
            articles={plp.recent_articles}
            viewMoreNewsPath={plp.view_more_news_path}
          />
        )}
        {plp.content && plp.content.length > 0 && (
          <div className="content-width flex flex-col gap-8 py-8 lg:py-12 [&_p]:text-sm [&_p]:text-grey-read">
            <Content content={plp.content.slice(introParagraphs ? 1 : 0)} />
          </div>
        )}
      </div>
      {showAppBanner && (
        <DownloadAppBlock
          downloadLink="https://thesolesupplier.onelink.me/BPkz/8481irlk"
          qrCode="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/download_app_homepage_qr_code.png"
          pagePosition="PLP App Banner"
        />
      )}
    </>
  );
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction
}) => {
  if (formAction?.startsWith('/bolt')) return false;

  return defaultShouldRevalidate;
};
